// src/billing/planGroups.js — the plans grid's SELECTION layer.
//
// ⛔⛔ THIS MODULE CHOOSES WHICH SERVER ROW IS ON DISPLAY. It never builds a row,
// never merges two, never computes a price. `options[]` from
// GET /billing/subscription is the catalogue; a toggle position is an index into
// it. A card showing one row's price beside another row's button is exactly the
// defect the v171 rewrite removed.
//
// ⛔ NO DECIMAL PARSING ANYWHERE. The backend sorts `options[]` by price ascending
// (billing_subscription.py `_option_sort_key`), so every ordering needed here —
// family order, axis order, "the cheapest actionable row" — is FIRST APPEARANCE
// in the given array.

/**
 * A row that cannot be placed on an axis of the grid.
 *
 * ⭐ STRUCTURAL, never semantic: the question is "can this row sit at a cell?",
 * not "is this a trial?". `free` qualifies because its 14-day period is not in
 * the backend's interval map ({30:'month', 365:'year'}) so `billing_interval` is
 * null; `special` qualifies twice (no family, no workspace cap).
 * ⛔ `period_days` is never read — the contract says group and label by
 * `billing_interval`, never by it.
 */
export function isLoose(option) {
  return option.billing_interval == null
    || option.plan_family == null
    || option.max_tenants == null;
}

export function cellKey(interval, maxTenants) {
  return `${interval}::${maxTenants}`;
}

/** Distinct values in the order they first appear (= price ascending). */
function firstAppearance(rows, read) {
  const seen = [];
  rows.forEach((r) => {
    const v = read(r);
    if (!seen.includes(v)) seen.push(v);
  });
  return seen;
}

/**
 * Split the VISIBLE rows (the caller has already dropped HIDDEN_REASON_CODES)
 * into the grid's families plus the loose rows.
 */
export function groupPlanOptions(rows) {
  const list = rows || [];
  const placeable = list.filter((r) => !isLoose(r));
  const loose = list.filter(isLoose);

  const intervals = firstAppearance(placeable, (r) => r.billing_interval);
  const workspaces = firstAppearance(placeable, (r) => r.max_tenants);

  const families = [];
  const byFamily = new Map();
  placeable.forEach((r) => {
    let f = byFamily.get(r.plan_family);
    if (!f) {
      // ⭐ Family order = first appearance = the server's price-ascending order.
      // Computed from the whole list, NOT from the selected cell, so the cards
      // keep their order across every toggle flip.
      f = { family: r.plan_family, rows: [], byCell: new Map(), collides: false };
      byFamily.set(r.plan_family, f);
      families.push(f);
    }
    f.rows.push(r);
    const k = cellKey(r.billing_interval, r.max_tenants);
    // ⛔ COLLISION: two visible rows want the same cell. One of them would become
    // unreachable, which on this screen means HIDING A BUYABLE PLAN. The family
    // falls back to a flat list instead — see PlanGrid.
    if (f.byCell.has(k)) f.collides = true;
    else f.byCell.set(k, r);
  });

  return { families, loose, intervals, workspaces };
}

/**
 * Where the toggles open.
 *
 * ⚠️ A CHAIN, not a lookup: the plan in force is not always a visible row — a
 * free/special customer's own plan is among the HIDDEN ones — and it may be
 * loose even when visible.
 */
export function seedPosition(rows, currentPlanId) {
  const placeable = (rows || []).filter((r) => !isLoose(r));
  const pick = placeable.find((r) => r.plan_id === currentPlanId)
    || placeable.find((r) => r.action !== 'unavailable')
    || placeable[0];
  return pick
    ? { interval: pick.billing_interval, workspaces: pick.max_tenants }
    : null;
}

/**
 * The position actually in force: the user's choice while it still exists in the
 * data, otherwise the seed.
 *
 * ⛔⛔ CALL THIS EVERY RENDER. `useState(seedPosition(...))` snapshots the seed,
 * and `reload({ fresh: true })` runs after EVERY successful change — a frozen
 * seed leaves the card showing the plan the user just left.
 */
export function resolvePosition({ choice, seed, intervals, workspaces }) {
  if (!seed) return null;
  return {
    interval: intervals.includes(choice?.interval) ? choice.interval : seed.interval,
    workspaces: workspaces.includes(choice?.workspaces) ? choice.workspaces : seed.workspaces,
  };
}
